// -----------------------------------------------------------------------------
// Schema Designer — DTOs + fetcher helpers (Phase 5 Wave 2, agent-ui-schema).
//
// Local to this feature so we don't trample on src/api/types.ts (owned by the
// shell). When/if these types stabilise we'll let the root lift the document-
// type shapes into the shared types module.
// -----------------------------------------------------------------------------

import { ApiError, apiFetch } from '../../api/client';
import { Endpoints } from '../../api/types';

// ---------- Enums (mirror Domain/Enums/*.cs) ----------

/** FieldType — must match DocuSense.Domain.Enums.FieldType names (PascalCase). */
export const FieldTypes = {
  String: 'String',
  Decimal: 'Decimal',
  Integer: 'Integer',
  Date: 'Date',
  Boolean: 'Boolean',
  Table: 'Table',
  Group: 'Group',
} as const;
export type FieldType = (typeof FieldTypes)[keyof typeof FieldTypes];

export const FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: FieldType; label: string }> = [
  { value: 'String', label: 'Texto' },
  { value: 'Decimal', label: 'Decimal' },
  { value: 'Integer', label: 'Entero' },
  { value: 'Date', label: 'Fecha' },
  { value: 'Boolean', label: 'Booleano' },
  { value: 'Table', label: 'Tabla (filas repetidas)' },
  { value: 'Group', label: 'Grupo (sub-campos anidados)' },
];

/**
 * Subset of FieldType valid for Table columns AND Group children — neither nested tables nor
 * nested groups are allowed (the domain rejects both). One source of truth here keeps the
 * Schema editor's child selectors in sync.
 */
export type ScalarFieldType = Exclude<FieldType, 'Table' | 'Group'>;

export const SCALAR_FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: ScalarFieldType; label: string }> =
  FIELD_TYPE_OPTIONS.filter((o) => o.value !== 'Table' && o.value !== 'Group') as ReadonlyArray<{
    value: ScalarFieldType;
    label: string;
  }>;

/**
 * Subset valid for Group CHILDREN: scalars + Table (groups can contain tables, but not nested
 * groups per the V1 cap). Used by the schema editor's child-row type selector.
 */
export const GROUP_CHILD_FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: FieldType; label: string }> =
  FIELD_TYPE_OPTIONS.filter((o) => o.value !== 'Group');

/** ValidationRule — must match DocuSense.Domain.Enums.ValidationRule names. */
export const ValidationRules = {
  None: 'None',
  IbanChecksum: 'IbanChecksum',
  TaxIdChecksum: 'TaxIdChecksum',
  SumOfChildren: 'SumOfChildren',
  DateRangeCoherent: 'DateRangeCoherent',
} as const;
export type ValidationRule = (typeof ValidationRules)[keyof typeof ValidationRules];

export const VALIDATION_RULE_OPTIONS: ReadonlyArray<{ value: ValidationRule; label: string }> = [
  { value: 'None', label: 'Ninguna' },
  { value: 'IbanChecksum', label: 'Checksum IBAN' },
  { value: 'TaxIdChecksum', label: 'Checksum NIF/CIF' },
  { value: 'SumOfChildren', label: 'Suma de campos hijos' },
  { value: 'DateRangeCoherent', label: 'Rango de fechas coherente' },
];

/**
 * OcrModelTier — must match DocuSense.Domain.Enums.OcrModelTier names. Controls which Azure
 * Document Intelligence model is used for OCR. Auto (default) lets the system pick based on
 * whether the schema contains table fields.
 */
export type OcrModelTier = 'Auto' | 'TextOnly' | 'Tables' | 'Full';

export const OCR_MODEL_TIER_OPTIONS: ReadonlyArray<{ value: OcrModelTier; label: string }> = [
  { value: 'Auto', label: 'Automático (recomendado)' },
  { value: 'TextOnly', label: 'Solo texto — más barato (sin tablas)' },
  { value: 'Tables', label: 'Texto + tablas' },
  { value: 'Full', label: 'Completo (tablas + pares clave/valor)' },
] as const;

/**
 * OcrCapability — must match DocuSense.Domain.Enums.OcrCapabilities member names. Used when the OCR
 * source is a platform provider (e.g. Mistral) instead of the per-license Document Intelligence default.
 */
export type OcrCapability = 'Text' | 'Tables' | 'Structure' | 'KeyValuePairs';

export const OCR_CAPABILITY_OPTIONS: ReadonlyArray<{ value: OcrCapability; label: string }> = [
  { value: 'Text', label: 'Texto' },
  { value: 'Tables', label: 'Tablas' },
  { value: 'Structure', label: 'Estructura' },
  { value: 'KeyValuePairs', label: 'Pares clave/valor' },
];

/** OcrProviderKind enum name — mirrors DocuSense.Domain.Enums.OcrProviderKind. */
export type OcrProviderKind = 'AzureDocumentIntelligence' | 'Mistral';

/**
 * SegmentationStrategy — must match DocuSense.Domain.Enums.SegmentationStrategy names. Sent/received
 * as the string enum name (the API registers a JsonStringEnumConverter), not the integer.
 */
export type SegmentationStrategy = 'AiBoundary' | 'IdentifierRecurrence';

export const SEGMENTATION_STRATEGY_OPTIONS: ReadonlyArray<{
  value: SegmentationStrategy;
  label: string;
}> = [
  { value: 'AiBoundary', label: 'IA — detecta límites en cualquier tipo (recomendado)' },
  { value: 'IdentifierRecurrence', label: 'Identificador recurrente por página (determinista)' },
];

/** Mirrors SegmentationConfig.DefaultMaxSegments — the default safety ceiling on sub-documents. */
export const DEFAULT_MAX_SEGMENTS = 50;

// ---------- DTOs (mirror DocuSense.Application.Dtos.DocumentTypes / TenantModelProviders) ----------

export interface TableColumnResponse {
  name: string;
  type: string; // FieldType enum name (scalar)
  required: boolean;
  pattern?: string | null;
  hint?: string | null;
}

export interface TableColumnRequest {
  name: string;
  type: ScalarFieldType;
  required: boolean;
  pattern?: string | null;
  hint?: string | null;
}

export interface FieldDefinitionResponse {
  name: string;
  type: string; // FieldType enum name
  required: boolean;
  critical: boolean;
  pattern?: string | null;
  validationRule: string; // ValidationRule enum name
  childFields?: string[] | null;
  /** Free-text guidance for the AI prompt (Wave 7.A). */
  hint?: string | null;
  /** Present when type === "Table" (Wave 7.B); null/omitted otherwise. */
  columns?: TableColumnResponse[] | null;
  /** Present when type === "Group" (Wave 12); null/omitted otherwise. Children themselves are never groups. */
  children?: FieldDefinitionResponse[] | null;
}

export interface FieldDefinitionRequest {
  name: string;
  type: FieldType;
  required: boolean;
  critical: boolean;
  pattern?: string | null;
  validationRule: ValidationRule;
  childFields?: string[] | null;
  /** Free-text guidance for the AI prompt (Wave 7.A); max 500 chars enforced server-side. */
  hint?: string | null;
  /** Required (>=1, <=50) when type === "Table"; must be null/omitted otherwise. */
  columns?: TableColumnRequest[] | null;
  /** Required (>=1, <=50) when type === "Group"; must be null/omitted otherwise. */
  children?: FieldDefinitionRequest[] | null;
}

export interface ConfidenceThresholdsResponse {
  fieldWarning: number;
  fieldCritical: number;
  documentReviewRatio: number;
  documentRejectGlobalScore: number;
}

/** Mirrors DocuSense.Domain.Entities.ConfidenceThresholds value object. */
export interface ConfidenceThresholdsRequest {
  fieldWarning: number;
  fieldCritical: number;
  documentReviewRatio: number;
  documentRejectGlobalScore: number;
}

/** Mirrors DocuSense.Application.Dtos.DocumentTypes.SegmentationResponse. */
export interface DocumentSegmentationResponse {
  enabled: boolean;
  /** SegmentationStrategy enum name. */
  strategy: string;
  identifierFieldName?: string | null;
  identifierPattern?: string | null;
  maxSegments: number;
}

/**
 * Mirrors the DocuSense.Domain.Entities.SegmentationConfig the update endpoint binds. `enabled` is
 * required server-side; `strategy` and `maxSegments` are non-nullable (never send null) — the API's
 * JsonStringEnumConverter accepts the strategy as its string name.
 */
export interface DocumentSegmentationRequest {
  enabled: boolean;
  strategy: SegmentationStrategy;
  identifierFieldName?: string | null;
  identifierPattern?: string | null;
  maxSegments: number;
}

export interface DocumentTypeResponse {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  /** Strict `Major.Minor.Patch`; draft sentinel is `0.0.0`. */
  version: string;
  modelProviderId: string;
  isPublished: boolean;
  fields: FieldDefinitionResponse[];
  thresholds: ConfidenceThresholdsResponse;
  /** ISO 8601 instant of the last mutation (Wave 9.C). */
  updatedAt: string;
  /** Segmentation policy, or null/absent when the type never opted in (legacy single-document). */
  segmentation?: DocumentSegmentationResponse | null;
  /** OCR model tier override; null/absent means Auto (inferred from schema). */
  ocrModelTier?: OcrModelTier | null;
  /**
   * Discovery-derived signal that this type's documents are labelled key/value forms. When true and
   * the tier is Auto, extraction uses the richer document OCR tier. Null/absent means unknown
   * (manually authored type, or persisted before this column). Read-only / informational.
   */
  hasKeyValueLayout?: boolean | null;
  /**
   * Id of the platform OCR provider this type uses, or null for the per-license Azure Document
   * Intelligence default.
   */
  ocrProviderId?: string | null;
  /** Capability flag names to extract on the OCR-provider path (empty for the DI default). */
  ocrCapabilities?: string[];
  /** When true, this type is excluded from the auto-classification candidate catalogue. */
  excludeFromAutoClassification?: boolean;
}

/**
 * Body for PUT /api/v1/document-types/{slug} (create-or-update).
 * The server bumps the version and freezes a snapshot automatically — the
 * caller never sees or manages version numbers directly.
 */
export interface SaveDocumentTypeRequest {
  name: string;
  modelProviderId: string;
  fields: FieldDefinitionRequest[];
  thresholds?: ConfidenceThresholdsRequest | null;
  /** Segmentation policy; null/omitted leaves the existing policy untouched. */
  segmentation?: DocumentSegmentationRequest | null;
  /** OCR model tier override; null/omitted means Auto. */
  ocrModelTier?: OcrModelTier | null;
  /**
   * Id of the platform OCR provider to use, or null for the per-license Azure Document Intelligence
   * default. The editor always sends the full current selection (metadata-only — no version bump).
   */
  ocrProviderId?: string | null;
  /**
   * Capability flag names to extract when `ocrProviderId` is set. The editor ALWAYS sends this — an
   * empty array for the DI default — so it is never left untouched server-side.
   */
  ocrCapabilities?: string[];
  /**
   * When set, controls whether this type is excluded from the auto-classification candidate catalogue.
   * The editor always sends the current value so it is never left untouched. Metadata-only (no version bump).
   */
  excludeFromAutoClassification?: boolean;
  /**
   * Caller intent for slug collision enforcement.
   * `true`  — must NOT already exist (create); server returns 422 when the slug is taken.
   * `false` — must already exist (update); server returns 404 when the slug is absent.
   * omitted — upsert (legacy behavior, unaffected).
   */
  expectNew?: boolean;
}

/**
 * Body for POST /api/v1/document-types/{slug}/rollback.
 * Applies a prior version as a fresh forward version — versions only go up.
 */
export interface RollbackDocumentTypeRequest {
  toVersion: string;
}

export interface PromptPreviewResponse {
  slug: string;
  version: string;
  prompt: string;
  estimatedInputTokens: number;
}

export interface ModelProviderResponse {
  providerId: string;
  /** Anthropic / OpenAI / AzureOpenAI / OllamaLocal (string form). */
  type: string;
  alias: string;
  endpoint?: string | null;
  modelName?: string | null;
  isActive: boolean;
}

/**
 * Platform-managed OCR provider eligible for the tenant based on their license plan. Mirrors
 * DocuSense.Application.Dtos.TenantModelProviders.TenantOcrProviderResponse — minimal fields only
 * (no secrets/endpoint). Drives the OCR source selector in the document-type editor.
 */
export interface TenantOcrProviderResponse {
  id: string;
  displayName: string;
  /** OcrProviderKind enum name (PascalCase). */
  kind: OcrProviderKind;
  modelId?: string | null;
  /** Capability flag names this resource is configured to extract (defaults for the doctype). */
  capabilities: string[];
}

/** Platform-managed AI provider eligible for the tenant based on their license plan. Mirrors PlatformConnectionResponse.cs. */
export interface PlatformConnectionResponse {
  providerId: string;
  /** ProviderType enum name (PascalCase). */
  providerType: string;
  displayName: string;
  modelName: string;
  endpoint?: string | null;
  /** Monthly call limit granted by the plan; null = unlimited. */
  monthlyCallLimit: number | null;
}

// ---------- Fetchers ----------

export function listDocumentTypes(signal?: AbortSignal): Promise<DocumentTypeResponse[]> {
  return apiFetch<DocumentTypeResponse[]>(Endpoints.documentTypes, signal !== undefined ? { signal } : {});
}

/** Caller intent for `GET /api/v1/document-types/{slug}`. */
export type DocumentTypePreference = 'draft' | 'published';

export interface GetDocumentTypeOptions {
  /** When 'draft', the API returns the in-progress draft (falling back to published). */
  prefer?: DocumentTypePreference;
  signal?: AbortSignal;
}

export function getDocumentTypeBySlug(
  slug: string,
  options: GetDocumentTypeOptions | AbortSignal = {},
): Promise<DocumentTypeResponse> {
  // Legacy callers pass an AbortSignal directly (Wave 5 / Wave 9.B). Keep backwards compatibility.
  const normalized: GetDocumentTypeOptions =
    options instanceof AbortSignal ? { signal: options } : options;
  const query = normalized.prefer !== undefined ? `?prefer=${encodeURIComponent(normalized.prefer)}` : '';
  return apiFetch<DocumentTypeResponse>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}${query}`,
    normalized.signal !== undefined ? { signal: normalized.signal } : {},
  );
}

export function listDocumentTypeVersions(
  slug: string,
  signal?: AbortSignal,
): Promise<DocumentTypeResponse[]> {
  return apiFetch<DocumentTypeResponse[]>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/versions`,
    signal !== undefined ? { signal } : {},
  );
}

/**
 * PUT /api/v1/document-types/{slug} — create-or-update in one call.
 * The server bumps the version automatically; callers never need to distinguish
 * between "create" and "update" — the slug determines which path applies.
 */
export function saveDocumentType(
  slug: string,
  body: SaveDocumentTypeRequest,
): Promise<DocumentTypeResponse> {
  return apiFetch<DocumentTypeResponse>(`${Endpoints.documentTypes}/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    json: body,
  });
}

/**
 * POST /api/v1/document-types/{slug}/rollback — restore a prior version as a
 * new forward version. Versions only ever go up; rollback applies the old
 * version's schema as a fresh increment.
 */
export function rollbackDocumentType(
  slug: string,
  toVersion: string,
): Promise<DocumentTypeResponse> {
  return apiFetch<DocumentTypeResponse>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/rollback`,
    { method: 'POST', json: { toVersion } satisfies RollbackDocumentTypeRequest },
  );
}

export function deleteDocumentType(slug: string): Promise<null> {
  return apiFetch<null>(`${Endpoints.documentTypes}/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

/**
 * Builds a `SaveDocumentTypeRequest` from an existing document type response,
 * stripping server-owned fields (id, tenantId, version, isPublished) and any
 * non-request properties carried by `FieldDefinitionResponse` / `TableColumnResponse`.
 *
 * Exported so the duplicate-mutation hook can unit-test the projection in isolation.
 */
/**
 * Maps one `FieldDefinitionResponse` to a `FieldDefinitionRequest`, RECURSIVELY
 * carrying Group `children` and Table `columns`. The children mapping is the fix
 * for duplicate: without it a Group field round-trips with no children and the
 * server rejects it ("Group field requires a non-null Children collection").
 */
function mapFieldResponseToRequest(field: FieldDefinitionResponse): FieldDefinitionRequest {
  const columns: TableColumnRequest[] | null =
    field.columns !== undefined && field.columns !== null
      ? field.columns.map((column) => ({
          name: column.name,
          // The response carries `type` as a free string (the enum name on the wire);
          // cast back to the scalar union — the server validator rejects anything else.
          type: column.type as ScalarFieldType,
          required: column.required,
          pattern: column.pattern ?? null,
          hint: column.hint ?? null,
        }))
      : null;

  const children: FieldDefinitionRequest[] | null =
    field.children !== undefined && field.children !== null
      ? field.children.map(mapFieldResponseToRequest)
      : null;

  return {
    name: field.name,
    type: field.type as FieldType,
    required: field.required,
    critical: field.critical,
    pattern: field.pattern ?? null,
    validationRule: field.validationRule as ValidationRule,
    childFields: field.childFields ?? null,
    hint: field.hint ?? null,
    columns,
    children,
  };
}

export function buildSaveRequestFromResponse(
  source: DocumentTypeResponse,
  overrides: { name: string },
): SaveDocumentTypeRequest {
  const fields: FieldDefinitionRequest[] = source.fields.map(mapFieldResponseToRequest);

  return {
    name: overrides.name,
    modelProviderId: source.modelProviderId,
    fields,
    thresholds: {
      fieldWarning: source.thresholds.fieldWarning,
      fieldCritical: source.thresholds.fieldCritical,
      documentReviewRatio: source.thresholds.documentReviewRatio,
      documentRejectGlobalScore: source.thresholds.documentRejectGlobalScore,
    },
  };
}

/**
 * UI-only helper — duplicates a document type under a new slug.
 *
 * Combines GET /document-types/{slug} with PUT /document-types/{newSlug} (the
 * unified save endpoint). The new type starts at v1.0.0 after the first save;
 * the caller provides the new slug (validated client-side against the slug regex).
 *
 * The new name defaults to `"{source.name} (copia)"` so the operator can rename
 * it later in the editor.
 */
export async function duplicateDocumentType(
  sourceSlug: string,
  newSlug: string,
): Promise<DocumentTypeResponse> {
  // PUT /{slug} is create-or-update, so duplicating onto an EXISTING slug would
  // silently OVERWRITE that type. Guard first: only proceed when the target slug
  // is free (a 404 from the lookup means "available").
  const taken = await getDocumentTypeBySlug(newSlug)
    .then(() => true)
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) {
        return false;
      }
      throw err;
    });
  if (taken) {
    throw new ApiError(422, `Ya existe un tipo de documento con el slug "${newSlug}".`, {
      kind: 'validation',
    });
  }

  const source = await getDocumentTypeBySlug(sourceSlug);
  const body = buildSaveRequestFromResponse(source, {
    name: `${source.name} (copia)`,
  });
  // expectNew=true is the server-side safety net — the GET pre-check above handles the instant UX
  // feedback, but a race condition between two concurrent duplicates could still reach the PUT.
  return saveDocumentType(newSlug, { ...body, expectNew: true });
}

export function getPromptPreview(
  slug: string,
  sampleText?: string,
  signal?: AbortSignal,
): Promise<PromptPreviewResponse> {
  const params = new URLSearchParams();
  if (sampleText !== undefined && sampleText !== '') {
    params.set('sampleText', sampleText);
  }
  const query = params.toString();
  const path =
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/prompt-preview` +
    (query !== '' ? `?${query}` : '');
  return apiFetch<PromptPreviewResponse>(path, signal !== undefined ? { signal } : {});
}

/**
 * Mirrors DocuSense.Application.Dtos.DocumentTypes.RebuildAnalyzerResponse —
 * payload returned by `POST /document-types/{slug}/content-understanding/analyzer/rebuild`.
 */
export interface RebuildAnalyzerResponse {
  documentTypeSlug: string;
  providerId: string;
  /** ISO 8601 instant of the rebuild. */
  rebuildAt: string;
}

/**
 * Forces a delete + recreate of the Content Understanding analyzer backing this
 * document type. Used to recover from a Failed analyzer state without bumping
 * the doctype version. The provider id in the response is the CU provider that
 * was rebuilt server-side.
 */
export function rebuildContentUnderstandingAnalyzer(
  slug: string,
  signal?: AbortSignal,
): Promise<RebuildAnalyzerResponse> {
  return apiFetch<RebuildAnalyzerResponse>(
    `${Endpoints.documentTypes}/${encodeURIComponent(slug)}/content-understanding/analyzer/rebuild`,
    {
      method: 'POST',
      ...(signal !== undefined ? { signal } : {}),
    },
  );
}

export function listConnections(signal?: AbortSignal): Promise<ModelProviderResponse[]> {
  return apiFetch<ModelProviderResponse[]>(
    Endpoints.connections,
    signal !== undefined ? { signal } : {},
  );
}

export function listPlatformConnections(signal?: AbortSignal): Promise<PlatformConnectionResponse[]> {
  return apiFetch<PlatformConnectionResponse[]>(
    `${Endpoints.connections}/platform`,
    signal !== undefined ? { signal } : {},
  );
}

/**
 * Lists the platform OCR providers the tenant's license grants access to (GET /api/v1/ocr-providers).
 * An empty array means the editor only offers the Azure Document Intelligence default.
 */
export function listAvailableOcrProviders(signal?: AbortSignal): Promise<TenantOcrProviderResponse[]> {
  return apiFetch<TenantOcrProviderResponse[]>(
    Endpoints.ocrProviders,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Diff helpers (used by VersionHistory) ----------

export type FieldDiffStatus = 'added' | 'removed' | 'modified' | 'unchanged';

export interface FieldDiff {
  name: string;
  status: FieldDiffStatus;
  /** True iff this field is `required` on the side it currently exists on. */
  isRequired: boolean;
  /** Human-readable summary of what changed (for `modified`). */
  detail?: string;
}

/** Comparable subset of a field definition. */
function fingerprint(field: FieldDefinitionResponse | FieldDefinitionRequest): string {
  const pattern = (field.pattern ?? '').toString();
  // Both DTO shapes type validationRule as string; the cast keeps the diff helper
  // resilient if the response gets re-typed as a stricter union later.
  const rule = String(field.validationRule);
  const childFields = (field.childFields ?? []).join(',');
  return [field.type, field.required, field.critical, pattern, rule, childFields].join('|');
}

/** Compares two field lists and returns a per-field diff used by VersionHistory. */
export function diffFieldLists(
  previous: ReadonlyArray<FieldDefinitionResponse | FieldDefinitionRequest>,
  next: ReadonlyArray<FieldDefinitionResponse | FieldDefinitionRequest>,
): FieldDiff[] {
  const prevByName = new Map<string, FieldDefinitionResponse | FieldDefinitionRequest>();
  for (const f of previous) {
    prevByName.set(f.name, f);
  }
  const result: FieldDiff[] = [];
  const seen = new Set<string>();

  for (const nextField of next) {
    seen.add(nextField.name);
    const prev = prevByName.get(nextField.name);
    if (prev === undefined) {
      result.push({ name: nextField.name, status: 'added', isRequired: nextField.required });
      continue;
    }
    const prevPrint = fingerprint(prev);
    const nextPrint = fingerprint(nextField);
    if (prevPrint === nextPrint) {
      result.push({ name: nextField.name, status: 'unchanged', isRequired: nextField.required });
    } else {
      result.push({
        name: nextField.name,
        status: 'modified',
        isRequired: nextField.required,
        detail: describeFieldChange(prev, nextField),
      });
    }
  }
  for (const prevField of previous) {
    if (!seen.has(prevField.name)) {
      result.push({ name: prevField.name, status: 'removed', isRequired: prevField.required });
    }
  }
  return result;
}

function describeFieldChange(
  prev: FieldDefinitionResponse | FieldDefinitionRequest,
  next: FieldDefinitionResponse | FieldDefinitionRequest,
): string {
  const changes: string[] = [];
  if (prev.type !== next.type) {
    changes.push(`tipo ${String(prev.type)} → ${String(next.type)}`);
  }
  if (prev.required !== next.required) {
    changes.push(next.required ? 'ahora obligatorio' : 'ahora opcional');
  }
  if (prev.critical !== next.critical) {
    changes.push(next.critical ? 'ahora crítico' : 'ya no crítico');
  }
  if ((prev.pattern ?? '') !== (next.pattern ?? '')) {
    changes.push('regex modificada');
  }
  if (String(prev.validationRule) !== String(next.validationRule)) {
    changes.push(`validación ${String(prev.validationRule)} → ${String(next.validationRule)}`);
  }
  return changes.join(', ');
}

export type VersionBump = 'major' | 'minor' | 'patch';

export interface PublishImpact {
  bump: VersionBump;
  nextVersion: string;
  diffs: FieldDiff[];
}

/** Parses a strict semver string. Returns null when malformed. */
export function parseSemver(version: string): [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  if (match === null) {
    return null;
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function formatSemver(parts: [number, number, number]): string {
  return `${parts[0]}.${parts[1]}.${parts[2]}`;
}

/**
 * Decides the bump to apply when publishing `draft` over `lastPublished`.
 *
 *   MAJOR — any required field added or removed.
 *   MINOR — any non-required field added or removed.
 *   PATCH — only validation/regex/criticality/type changes on existing fields.
 *
 * When there's no `lastPublished` version yet, returns `1.0.0` as `nextVersion`.
 */
export function computePublishImpact(
  draft: DocumentTypeResponse,
  lastPublished: DocumentTypeResponse | null,
): PublishImpact {
  if (lastPublished === null) {
    const diffs = draft.fields.map<FieldDiff>((f) => ({
      name: f.name,
      status: 'added',
      isRequired: f.required,
    }));
    return { bump: 'major', nextVersion: '1.0.0', diffs };
  }

  const diffs = diffFieldLists(lastPublished.fields, draft.fields);
  const hasRequiredAddedOrRemoved = diffs.some(
    (d) => (d.status === 'added' || d.status === 'removed') && d.isRequired,
  );
  const hasOptionalAddedOrRemoved = diffs.some(
    (d) => (d.status === 'added' || d.status === 'removed') && !d.isRequired,
  );
  const hasModifications = diffs.some((d) => d.status === 'modified');

  const parts = parseSemver(lastPublished.version) ?? [1, 0, 0];
  let bump: VersionBump;
  let nextParts: [number, number, number];
  if (hasRequiredAddedOrRemoved) {
    bump = 'major';
    nextParts = [parts[0] + 1, 0, 0];
  } else if (hasOptionalAddedOrRemoved) {
    bump = 'minor';
    nextParts = [parts[0], parts[1] + 1, 0];
  } else if (hasModifications) {
    bump = 'patch';
    nextParts = [parts[0], parts[1], parts[2] + 1];
  } else {
    // No changes detected — still emit a patch bump so the user can publish
    // a fresh draft over an identical previous version (rare but possible).
    bump = 'patch';
    nextParts = [parts[0], parts[1], parts[2] + 1];
  }
  return { bump, nextVersion: formatSemver(nextParts), diffs };
}

// ---------- Query keys ----------

export const SchemaQueryKeys = {
  all: ['document-types'] as const,
  list: () => [...SchemaQueryKeys.all, 'list'] as const,
  /**
   * Detail of a document type. There is no longer a draft/published split at the API
   * level — GET always returns the head. The `prefer` parameter is accepted for
   * backwards compatibility but ignored at the server.
   */
  detail: (slug: string, _prefer?: DocumentTypePreference) =>
    [...SchemaQueryKeys.all, 'detail', slug] as const,
  versions: (slug: string) => [...SchemaQueryKeys.all, 'versions', slug] as const,
  promptPreview: (slug: string, sample: string | undefined) =>
    [...SchemaQueryKeys.all, 'prompt-preview', slug, sample ?? ''] as const,
  connections: ['connections', 'list'] as const,
  platformConnections: ['connections', 'platform'] as const,
  ocrProviders: ['ocr-providers', 'available'] as const,
};
